import React, { useRef, useEffect } from 'react';
import styles from './plannerView.module.scss';
import { LoadsCard } from './loadsCard/loadsCard';
import { DateTime } from "luxon";
import { truckLineHeight } from './truckCard/truckCard';
import { useBoardStore } from '../boardStore';
export const DAY_WIDTH_IN_PIXELS = 250;
export const DEFAULT_PLANNER_SCROLL_LEFT = 700;

export function getSharedScrollLeft(trucksSetDataMethodsObject) {
    return trucksSetDataMethodsObject?.sharedScrollLeft ?? DEFAULT_PLANNER_SCROLL_LEFT;
}

export function applyCurrentTimeXToScrollViews(trucksSetDataMethodsObject, xPos) {
    if (xPos == null || !trucksSetDataMethodsObject) return;
    const x = `${xPos}px`;
    const views = trucksSetDataMethodsObject.scrollableViews || {};
    for (const key of Object.keys(views)) {
        const el = views[key];
        if (el) el.style.setProperty('--current-time-x', x);
    }
}

export function syncAllPlannerScrollViews(trucksSetDataMethodsObject) {
    if (!trucksSetDataMethodsObject) return;
    const scrollLeft = getSharedScrollLeft(trucksSetDataMethodsObject);
    const views = trucksSetDataMethodsObject.scrollableViews || {};
    for (const key of Object.keys(views)) {
        const el = views[key];
        if (el) el.scrollLeft = scrollLeft;
    }
    trucksSetDataMethodsObject.sharedScrollLeft = scrollLeft;
}

function notifyPlannerTimeShifted(trucksSetDataMethodsObject) {
    if (!trucksSetDataMethodsObject) return;
    for (const key of Object.keys(trucksSetDataMethodsObject)) {
        if (key.startsWith("CLTD") || key.startsWith("UPTD")) {
            trucksSetDataMethodsObject[key]("timeHasShifted");
        } else if (key === "trucksWithTimeDependentTasks") {
            const updateTasksObject = trucksSetDataMethodsObject[key];
            const nowMs = Date.now();
            for (const subkey of Object.keys(updateTasksObject)) {
                if (nowMs - updateTasksObject[subkey].lastUpdate >= (60 * 1000) * 30) {
                    updateTasksObject[subkey].updateTasks();
                }
            }
        }
    }
}

function updateHeaderTimeLabel(trucksSetDataMethodsObject, currentTimeRef) {
    const header = trucksSetDataMethodsObject?.headerTimeMarker;
    if (!header || !currentTimeRef?.current) return;
    const label = header.labelEl || header.markerEl?.firstElementChild;
    if (label) {
        label.innerText = currentTimeRef.current.toFormat('HH:mm');
    }
}

function advanceCurrentTimeRef(currentTimeRef) {
    if (!currentTimeRef) return;
    const live = useBoardStore.getState().getLiveCurrentTime?.();
    if (live) {
        currentTimeRef.current = live;
        return;
    }
    // Keep prior value if anchor missing; never sync to DateTime.now().
}

export function getCurrentTimeMarkerX(currentTime, beggingingOfDyMl) {
    const diffInMs = currentTime.current.toMillis() - beggingingOfDyMl;
    const diffInMinutes = Math.round(diffInMs / (1000 * 60));
    return (1000 + (diffInMinutes * DAY_WIDTH_IN_PIXELS / 1440));
}

/** Single source of truth: advance time once, update X on all scroll rows + header label. */
export function refreshPlannerTimeMarker(currentTimeRef, beggingingOfDyMl, trucksSetDataMethodsObject) {
    try {
        advanceCurrentTimeRef(currentTimeRef);
        try {
            const bumpPlannerTick = useBoardStore.getState().bumpPlannerTick;
            if (typeof bumpPlannerTick === 'function') bumpPlannerTick();
        } catch (e) {}
        notifyPlannerTimeShifted(trucksSetDataMethodsObject);

        const xPos = getCurrentTimeMarkerX(currentTimeRef, beggingingOfDyMl);
        if (trucksSetDataMethodsObject) {
            trucksSetDataMethodsObject.currentTimeMarkerX = xPos;
        }
        applyCurrentTimeXToScrollViews(trucksSetDataMethodsObject, xPos);
        updateHeaderTimeLabel(trucksSetDataMethodsObject, currentTimeRef);

        const headerMarker = trucksSetDataMethodsObject?.headerTimeMarker?.markerEl;
        if (headerMarker) {
            headerMarker.dataset.xPos = String(xPos);
        }
    } catch (e) {
        console.log(e);
    }
}

export function onPlannerScrollViewMounted(el, scrollKey, trucksSetDataMethodsObject, handleScroll, currentTimeRef, beggingingOfDyMl) {
    el.scrollLeft = getSharedScrollLeft(trucksSetDataMethodsObject);
    trucksSetDataMethodsObject.scrollableViews[scrollKey] = el;
    el.addEventListener('wheel', handleScroll, { passive: false });

    if (trucksSetDataMethodsObject.currentTimeMarkerX != null) {
        el.style.setProperty('--current-time-x', `${trucksSetDataMethodsObject.currentTimeMarkerX}px`);
    } else {
        refreshPlannerTimeMarker(currentTimeRef, beggingingOfDyMl, trucksSetDataMethodsObject);
    }
}

export function onPlannerScrollViewUnmounted(el, scrollKey, trucksSetDataMethodsObject, handleScroll) {
    delete trucksSetDataMethodsObject.scrollableViews[scrollKey];
    el?.removeEventListener('wheel', handleScroll, false);
}

/** @deprecated Use refreshPlannerTimeMarker */
export function setCurrentTimeMarker(markerEl, labelEl, currentTime, beggingingOfDyMl, trucksSetDataMethodsObject) {
    if (trucksSetDataMethodsObject) {
        trucksSetDataMethodsObject.headerTimeMarker = { markerEl, labelEl };
    }
    refreshPlannerTimeMarker(currentTime, beggingingOfDyMl, trucksSetDataMethodsObject);
}

export const PlannerViewTeamCard = ({teamIndex, teamData, truckIds, currentTime, loadListsContainer, trucksSetDataMethodsObject, popupViewManagingObject, overflowContainer, handleScroll, currentTimeRef, beggingingOfDyMl}) => {
    const orderedIds = React.useMemo(() => truckIds || teamData?.truckIds || [], [truckIds, teamData?.truckIds]);
    
    const trucksById = useBoardStore(s => s.trucksById);
    
    const trucks = React.useMemo(() => {
        return orderedIds.map((id) => trucksById?.[id]).filter(Boolean);
    }, [orderedIds, trucksById]);

    const scrollKey = teamData?.teamName ? `team:${teamData.teamName}` : `team:${teamIndex}`;

    return <FollowingPlannerViewTeamCard
        scrollKey={scrollKey}
        loadListsContainer={loadListsContainer}
        trucksSetDataMethodsObject={trucksSetDataMethodsObject}
        trucks={trucks}
        currentTime={currentTime}
        popupViewManagingObject={popupViewManagingObject}
        overflowContainer={overflowContainer}
        handleScroll={handleScroll}
        currentTimeRef={currentTimeRef}
        beggingingOfDyMl={beggingingOfDyMl}
    />
}

export const Timeline = ({currentTime, trucksSetDataMethodsObject, handleScroll, currentTimeRef, beggingingOfDyMl }) => {
    const lastElRef = useRef(null);
    const markerRef = useRef(null);
    const labelRef = useRef(null);

    useEffect(() => {
        trucksSetDataMethodsObject.headerTimeMarker = {
            markerEl: markerRef.current,
            labelEl: labelRef.current,
        };
        updateHeaderTimeLabel(trucksSetDataMethodsObject, currentTimeRef);
    });

    const handleRef = React.useCallback((el) => {
        if (lastElRef.current && lastElRef.current !== el) {
            onPlannerScrollViewUnmounted(lastElRef.current, '0', trucksSetDataMethodsObject, handleScroll);
        }
        if (!el) {
            lastElRef.current = null;
            trucksSetDataMethodsObject.headerTimeMarker = null;
            return;
        }
        lastElRef.current = el;
        onPlannerScrollViewMounted(el, '0', trucksSetDataMethodsObject, handleScroll, currentTimeRef, beggingingOfDyMl);
    // eslint-disable-next-line
    }, []);

    const timeLineArray = [];
    let timeLineStart = DateTime.fromISO(currentTime, { setZone: true }).startOf('day');
    timeLineStart = timeLineStart.minus({ days: 4 });
    for (let i = 0; i < 9; i++) {
        timeLineArray.push(timeLineStart.plus({ days: i }).toFormat('ccc MM/dd'));
    }

    return <>
        <div id="horizontallyScrollable" className={`${styles.loadPlannerViewLoadsSection} ${styles.header}`} style={{ height: `100%` }} ref={handleRef}>
            <div className={styles.timeline}>
                {timeLineArray.map((i, index) => (
                    <span key={index} className={styles.event}>
                        <div>{i}</div>
                        <div className={styles.tlbl}>
                            <div>00:00</div>
                            <div>06:00</div>
                            <div>12:00</div>
                            <div>18:00</div>
                        </div>
                    </span>
                ))}
            </div>
            <span ref={markerRef} className={styles.timeMarker}>
                <div ref={labelRef} className={styles.crntTimeLbl} />
            </span>
        </div>
    </>
}

export const FirstPlannerViewTeamCard = ({index, teamData, truckIds, currentTime, loadListsContainer, trucksSetDataMethodsObject, popupViewManagingObject, overflowContainer, handleScroll, currentTimeRef, beggingingOfDyMl }) => {
    const trucksById = useBoardStore(s => s.trucksById);
    const orderedIds = truckIds || teamData?.truckIds || [];
    const trucks = (orderedIds || []).map((id) => trucksById?.[id]).filter(Boolean);
    const scrollKey = teamData?.teamName ? `team:${teamData.teamName}` : `team:${index}`;
    const lastElRef = useRef(null);

    const handleRef = React.useCallback((el) => {
        if (lastElRef.current && lastElRef.current !== el) {
            onPlannerScrollViewUnmounted(lastElRef.current, scrollKey, trucksSetDataMethodsObject, handleScroll);
        }
        if (!el) {
            lastElRef.current = null;
            return;
        }
        lastElRef.current = el;
        onPlannerScrollViewMounted(el, scrollKey, trucksSetDataMethodsObject, handleScroll, currentTimeRef, beggingingOfDyMl);
    // eslint-disable-next-line
    }, []);

    const timeLineArray = [];
    let timeLineStart = DateTime.fromISO(currentTime, { setZone: true }).startOf('day');
    timeLineStart = timeLineStart.minus({ days: 4 });
    for (let i = 0; i < 9; i++) {
        timeLineArray.push(timeLineStart.plus({ days: i }).toFormat('ccc MM/dd'));
    }

    return <>
        <div id="horizontallyScrollable" className={styles.loadPlannerViewLoadsSection} style={{ maxHeight: `${(trucks.length * truckLineHeight) + 2.9}rem` }} ref={handleRef}>
            <div className={styles.timeline}>
                {timeLineArray.map((i, index) => (
                    <span key={index} className={styles.event}>
                        <div>{i}</div>
                        <div className={styles.tlbl}>
                            <div>00:00</div>
                            <div>06:00</div>
                            <div>12:00</div>
                            <div>18:00</div>
                        </div>
                    </span>
                ))}
            </div>
            <span className={styles.timeMarker}>
                <div className={styles.crntTimeLbl} />
            </span>
            <div className={styles.loadsContainer} ref={loadListsContainer}>
                {trucks.map((truck, truckIndex) => (
                    <LoadsCard key={truck.truckNumber || truckIndex} truckData={truck} overflowContainer={overflowContainer} trucksSetDataMethodsObject={trucksSetDataMethodsObject} timelineStartInMills={timeLineStart.toMillis()} currentTimeRef={currentTimeRef} popupViewManagingObject={popupViewManagingObject} />
                ))}
            </div>
        </div>
    </>
}

export const FollowingPlannerViewTeamCard = ({ scrollKey, trucks, currentTime, loadListsContainer, trucksSetDataMethodsObject, popupViewManagingObject, overflowContainer, handleScroll, currentTimeRef, beggingingOfDyMl }) => {
    const lastElRef = useRef(null);

    const handleRef = React.useCallback((el) => {
        if (lastElRef.current && lastElRef.current !== el) {
            onPlannerScrollViewUnmounted(lastElRef.current, scrollKey, trucksSetDataMethodsObject, handleScroll);
        }
        if (!el) {
            lastElRef.current = null;
            return;
        }
        lastElRef.current = el;
        onPlannerScrollViewMounted(el, scrollKey, trucksSetDataMethodsObject, handleScroll, currentTimeRef, beggingingOfDyMl);
    // eslint-disable-next-line
    }, []);

    let timeLineStart = DateTime.fromISO(currentTime, { setZone: true }).startOf('day');
    timeLineStart = timeLineStart.minus({ days: 4 });
    return <>
        <div id="horizontallyScrollable" className={styles.loadPlannerViewLoadsSection} style={{ maxHeight: `${(trucks.length * truckLineHeight) + 2.9}rem` }} ref={handleRef}>
            <div className={styles.emptyTimeline}>
                {Array.from({ length: 36 }).map((_, index) => (
                    <span key={index} className={styles.event} />
                ))}
            </div>
            <span className={styles.timeMarker} />
            <div className={`${styles.loadsContainer} ${styles.followingPlanner}`} ref={loadListsContainer}>
                {trucks.map((truck, truckIndex) => (
                    <LoadsCard key={truck.truckNumber || truckIndex} truckData={truck} overflowContainer={overflowContainer} trucksSetDataMethodsObject={trucksSetDataMethodsObject} timelineStartInMills={timeLineStart.toMillis()} currentTimeRef={currentTimeRef} popupViewManagingObject={popupViewManagingObject} />
                ))}
            </div>
        </div>
    </>
}
